import React, { Fragment, useState, useContext, useEffect, useRef, useReducer } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faKey, faEye, faPen, faTrash, faArrowLeft, faList, faShoppingCart, faCloudDownloadAlt, faCloudUploadAlt } from '@fortawesome/free-solid-svg-icons'
import { faCheckSquare, faSquare, faCircle, faDotCircle } from '@fortawesome/free-regular-svg-icons'
import AppContext from '../../../contexts/AppContext'
import { AsyncTypeahead } from 'react-bootstrap-typeahead'
import moment from 'moment'
import { bannerPlaceNameBy } from '../../../services/banners'

const bannerActionsAvailable = (banner, page) => {
    if(!banner) banner = { status: 0, has_been_paid: 0, is_placeholder: 0, can_be_shown: 0 }

    const isDraft = banner && banner.status === 0 ? true : false
    const isPlaceholder = banner && banner.is_placeholder > 0 ? true : false
    const isPublished = banner && banner.status === 2 ? true : false
    const hasBeenPaid = banner && banner.has_been_paid > 0 ? true : false
    const bannerId = banner && banner.id

    const canList = true
    const canBeShown = banner && banner.can_be_shown > 0 ? true : false
    const canBePaid = !hasBeenPaid && !isPublished
    const canEdit = [ 'view', 'pay', 'list' ].includes(page) && !banner.promo_id
    const canView = [ 'edit', 'pay', 'list' ].includes(page)
    const canSave = [ 'edit', 'create' ].includes(page)
    const canBuy = [ 'edit', 'create' ].includes(page) && !isPlaceholder && !hasBeenPaid && isDraft
    const linkToBuy = [ 'view', 'list' ].includes(page) && !isPlaceholder && !hasBeenPaid && isDraft
    const canDelete = [ 'list' ].includes(page) || isDraft
    const saveTitle = isDraft ? 'Save draft' : isPublished ? 'Save & send to review' : 'Save'

    const duration = banner.expires_at && banner.starts_at ? moment(banner.expires_at).diff(moment(banner.starts_at), 'days') + 1 : null

    return { canList, canEdit, canView, canSave, canBuy, canDelete, canBePaid, linkToBuy, bannerId, saveTitle, duration, isPlaceholder }

}

const BannerStatusBages = ({ banner }) => {

    if(!banner) banner = { status: 255, has_been_paid: 0 }
    const status_text = {
        [255]: 'New',
        0: 'Draft',
        1: 'Under review',
        2: 'Published',
        3: 'Rejected',
        4: 'Completed',
        5: 'Processing'
    }[banner.status]

	return (
		<Fragment>
			{banner && status_text &&
				<span
					className={
						'me-2 badge badge-' +
						{
							255: 'secondary',
							0: 'secondary',
							1: 'primary',
							2: 'success',
							3: 'warning',
							4: 'secondary',
                            5: 'secondary',
						}[banner.status]
					}
				>
					<small>{status_text}</small>
				</span>
			}
	        {banner && banner.can_be_shown > 0 && <span className="me-2 badge bg-success"><small>Enabled</small></span>}
	        {banner && banner.can_be_shown <= 0 && <span className="me-2 badge bg-secondary"><small>Paused</small></span>}
	        {banner && banner.has_been_paid > 0 && <span className="me-2 badge bg-success"><small>Paid</small></span>}
	        {banner && banner.has_been_paid <= 0 && banner.can_be_shown <= 0 && <span className="me-2 badge bg-danger"><small>Not Paid</small></span>}
		</Fragment>
	)
}

const BannerActionButtons = ({ banner, page, onSave, onOrder }) => {

    const { canList, canEdit, canView, canSave, canBuy, linkToBuy, bannerId, saveTitle } = bannerActionsAvailable(banner, page)

	const [ saving, setSaving ] = useState(false)
	const [ draftSaving, setDraftSaving ] = useState(false)

	// TODO: add this confirm before edit.
	// When you update your ad and it goes in for review it will not be available to view until the revision has been approved.

	return (
		<div>
			{canList &&
				<Link tag="a" to={'/account/banner/list'} className="btn btn-outline-secondary mb-3">
					<FontAwesomeIcon icon={faList} className="me-2" />
					<span className="d-none d-sm-inline">List</span>
				</Link>
			}
			{canEdit &&
				<Link tag="a" to={'/account/banner/edit/' + bannerId} className="btn btn-outline-primary mb-3 ms-2">
					<FontAwesomeIcon icon={faPen} className="me-2" />
					<span className="d-none d-sm-inline">Edit</span>
				</Link>
			}
			{canView &&
				<Link tag="a" to={'/account/banner/view/' + bannerId} className="btn btn-outline-primary mb-3 ms-2">
					<FontAwesomeIcon icon={faEye} className="me-2" />
					<span className="d-none d-sm-inline">View</span>
				</Link>
			}
			{canSave &&
                <button
                    disabled={draftSaving}
                    className="btn btn-outline-secondary mb-3 ms-2"
                    title={saveTitle}
                    onClick={() => {
                        if(typeof onSave == 'function'){
                            setDraftSaving(true)
                            onSave().then(res => {
                                setDraftSaving(false)
                            }).catch(res => {
                                setDraftSaving(false)
                            })
                        }
                    }}
                >
                    {!draftSaving && <FontAwesomeIcon icon={faCloudDownloadAlt} className="me-2" />}
                    {draftSaving && <FontAwesomeIcon icon={faSpinner} className="me-2" spin />}
					<span className="d-none d-sm-inline">Save</span>
                </button>
			}
			{linkToBuy &&
                <Link tag="a" to={'/account/banner/pay/' + bannerId} className="btn btn-success mb-3 ms-2">
                    <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
                    <span className="">Order</span>
                </Link>
			}
			{canBuy &&
                <button
                    disabled={saving}
                    className="btn btn-success mb-3 ms-2"
                    title="Save & Order"
                    onClick={() => {
                        if(typeof onOrder == 'function'){
                            setSaving(true)
                            onOrder(true).then(res => {
                                setSaving(false)
                            }).catch(res => {
                                setSaving(false)
                            })
                        }
                    }}
                >
                    {!saving && <FontAwesomeIcon icon={faShoppingCart} className="me-2" />}
                    {saving && <FontAwesomeIcon icon={faSpinner} spin className="me-2" />}
                    <span className="">Order</span>
                </button>
			}
		</div>
	)
}

const PlacementDetails = ({ details, total }) => {
    return (
        <table className="table mt-3">
            <thead>
                <tr>
                    <th>Banner type</th>
                    <th>Location, Area</th>
                    <th>Category</th>
                    <th>Duration</th>
                    <th>Price (credits)</th>
                </tr>
            </thead>
            <tbody>
                {details.map((item, i) => {
                    return (
                        <tr key={i}>
                            <td>{bannerPlaceNameBy(item.key)}</td>
                            <td>{item.location}</td>
                            <td>{item.category}</td>
                            <td>{item.duration}</td>
                            <td>{item.price}</td>
                        </tr>
                    )
                })}
            </tbody>
            <tfoot>
                <tr>
                    <th>Total:</th>
                    <th></th>
                    <th></th>
                    <th></th>
                    <th>{total}</th>
                </tr>
            </tfoot>
        </table>
    )
}

const PlacementTotal = ({ post_id, placement, categories, locations, onUpdated, isPlaceholder, startsAt, expiresAt, canBePaid }) => {

    const { bannerServices } = useContext(AppContext)
    const [ status, setStatus ] = useState(null)
    const [ isOpen, setIsOpen ] = useState(false)

    const locationIds = locations && locations.map(item => item.id)
    const categoryIds = placement.indexOf('category_') !== -1 ? categories : null
    const placement_key = (post_id ? (post_id + '_') : '') + placement + (categoryIds ? ('_categories_' + categoryIds.join('_')) : '') + (locationIds ? ('_locaions_' + locationIds.join('_')): '') + (startsAt ? ('_' + startsAt) : '') + (expiresAt ? ('_' + expiresAt) : '') + (isPlaceholder ? '-placeholder' : '')
    const canContinue = status && status.data ? true : false

    const loadDetails = () => {
        bannerServices.details(post_id, placement, categoryIds, locationIds, startsAt, expiresAt, isPlaceholder, canBePaid).then(res => {
            setStatus({data: res})
            onUpdated({data: res})
        }).catch(res => {
            if(typeof onUpdated == 'function'){
                setStatus({error: res})
                onUpdated({error: res})
            }
        })
    }

    useEffect(() => {
        loadDetails()
    }, [placement_key, canContinue])

    if((!canContinue && status && status.error) && canBePaid) return null

    const duration = expiresAt && startsAt ? moment(expiresAt).diff(moment(startsAt), 'days') + 1 : null

    return (
        <div className="card mb-3">
            <div className="card-body">
                {canContinue && status && status.data &&
                    <div>
                        <div className="d-flex justify-content-between flex-wrap">
                            {!isPlaceholder ? (
                                <div className="d-flex flex-wrap">
                                    <h3 className="card-title py-0 my-0 me-3">
                                        {status.data.total > 0 && <span className="badge bg-success me-2 my-1"><small>Total: {status.data.total}</small><small> credits</small></span>}
                                        {status.data.total == 0 && <span className="badge bg-secondary me-2 my-1"><small>Free Posting</small></span>}

                                        {placement &&
                                            <Fragment>
                                                {locationIds &&
                                                    <Fragment>
                                                        {locationIds.length > 0 && <span className="badge bg-secondary me-2 my-1"><small>{locationIds.length} Locations</small></span>}
                                                        {locationIds.length == 0 && <span className="badge bg-secondary me-2 my-1"><small>Global</small></span>}
                                                    </Fragment>
                                                }
                                                {categoryIds && categoryIds.length > 0 ? (
                                                    <span className="badge bg-secondary me-2 my-1"><small>{categoryIds.length} Categories</small></span>
                                                ) : (
                                                    <span className="badge bg-secondary me-2 my-1"><small>Home page</small></span>
                                                )}
                                            </Fragment>
                                        }
                                        {duration && <span className="badge bg-light text-dark border text-muted my-1"><small>Duration (days): {duration}</small></span>}
                                    </h3>
                                    <div className="d-flex flex-column justify-content-center my-2">
                                        <a href="" onClick={e => {
                                            e.preventDefault();
                                            setIsOpen(!isOpen)
                                        }}>
                                            {!isOpen ? 'Show more details' : 'Hide details'}
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <div></div>
                            )}
                        </div>

                        {isOpen && !isPlaceholder && <PlacementDetails details={status.data.details} total={status.data.total} />}

                    </div>
                }
            </div>
        </div>
    )
}

const PlacementTotalBy = ({ banner, page }) => {

    const { bannerServices } = useContext(AppContext)

    const [ details, setDetails ] = useState(null)
    const [ total, setTotal ] = useState(null)
    const [ isOpen, setIsOpen ] = useState(false)


    const bannerId = banner && banner.id

    const loadDetails = () => {
        bannerServices.detailsBy(bannerId).then(res => {
            setTotal(res.total)
            setDetails(res.details)
        }).catch(res => {
            console.log(res)
        })
    }

    useEffect(() => {
        if(bannerId) loadDetails()
    }, [ bannerId ])

    if(!banner) return null

    const placement = banner.placements && banner.placements[0] ? banner.placements[0] : null

    if(!placement) return null

    const categories = placement.categories
    const locations = placement.locations
    const locationIds = locations && locations.map(item => item.id)
    const categoryIds = categories && categories.map(item => item.id)
    const { duration, isPlaceholder } = bannerActionsAvailable(banner, page)

    return (
        <div className="card mb-3">
            <div className="card-body">
                <div className="d-flex justify-content-between flex-wrap">
                    {!isPlaceholder ? (
                        <div className="d-flex flex-wrap">
                            <h3 className="card-title py-0 my-0 me-3">
                                {total > 0 && <span className="badge bg-success me-2 my-1"><small>Total: {total}</small><small> credits</small></span>}
                                {total == 0 && <span className="badge bg-secondary me-2 my-1"><small>Free Posting</small></span>}

                                {placement &&
                                    <Fragment>
                                        {locationIds &&
                                            <Fragment>
                                                {locationIds.length > 0 && <span className="badge bg-secondary me-2 my-1"><small>{locationIds.length} Locations</small></span>}
                                                {locationIds.length == 0 && <span className="badge bg-secondary me-2 my-1"><small>Global</small></span>}
                                            </Fragment>
                                        }
                                        {categoryIds && categoryIds.length > 0 ? (
                                            <span className="badge bg-secondary me-2 my-1"><small>{categoryIds.length} Categories</small></span>
                                        ) : (
                                            <span className="badge bg-secondary me-2 my-1"><small>Home page</small></span>
                                        )}
                                    </Fragment>
                                }
                                {duration && <span className="badge bg-light text-dark border text-muted my-1"><small>Duration (days): {duration}</small></span>}
                            </h3>
                            <div className="d-flex flex-column justify-content-center my-2">
                                <a href="" onClick={e => {
                                    e.preventDefault();
                                    setIsOpen(!isOpen)
                                }}>
                                    {!isOpen ? 'Show more details' : 'Hide details'}
                                </a>
                            </div>
                        </div>
                    ) : (
                        <div></div>
                    )}
                </div>
                {isOpen && !isPlaceholder && details && <PlacementDetails details={details} total={total} />}
            </div>
        </div>
    )
}

const Checkbox = ({ label, children, active, onChange, disabled }) => {
    return (
        <div
            className={
                'my-1 ps-1' + (active ? ' bg-light-blue' : '') + (disabled ? ' text-muted' : ' cursor-pointer')
            }
            onClick={() => {
                if(typeof onChange == 'function' && !disabled){
                    onChange(!active)
                }
            }}
        >
            <span className={!active ? 'text-muted' : ''}>
                {active && <FontAwesomeIcon icon={faCheckSquare} />}
                {!active && <FontAwesomeIcon icon={faSquare} />}
                <span className="ms-2">{children}</span>
            </span>
        </div>
    )
}

const RadioButton = ({ label, children, active, onChange, disabled }) => {
    return (
        <div
            className={
                'my-1 ps-1' + (active ? ' bg-light-blue' : '') + (disabled ? ' text-muted' : ' cursor-pointer')
            }
            onClick={() => {
                if(typeof onChange == 'function' && !disabled){
                    onChange(!active)
                }
            }}
        >
            <span className={!active ? 'text-muted' : ''}>
                {active && <FontAwesomeIcon icon={faDotCircle} />}
                {!active && <FontAwesomeIcon icon={faCircle} />}
                <span className="ms-2">{children}</span>
            </span>
        </div>
    )
}

export {
	BannerStatusBages,
	BannerActionButtons,
	PlacementTotal,
    PlacementTotalBy,
	Checkbox,
	RadioButton,
    bannerActionsAvailable
}